import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Divider,
  Form,
  DatePicker,
  notification,
  Input,
  Descriptions,
  InputNumber,
  Upload,
  Radio,
} from 'antd';
import MESSAGES from '../../../enums/Messages';
import CustomButton from '../../../components/CustomButton';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import { Link } from 'react-router-dom';
import AssignmentStates, { ErrorsContent } from './AssignmentStates';
import SpinLoad from '../../../components/SpinLoad';
import {
  sendObservations,
  sendSign,
} from '../../../api/module/assign_postcontractual';
import {
  deleteEvidence,
  get,
  getEvidence,
  previewAct,
  put,
  uploadEvidence,
} from '../../../api/module/postcontractual';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { InboxOutlined } from '@ant-design/icons';
import { catchExceptionSRate } from '../../../utils/helpers/helpers';
import RichTextArea, {
  getRTRule,
  formatRTValue,
} from '../../../components/RichText';
import PdfViewer from '../../../components/PdfViewer';
import {
  getEdit,
  previewActEdit,
  putEdit,
} from '../../../api/module/pc_followup';

const { Title } = Typography;
const dateFormat = 'YYYY-MM-DD';
const dateFormatText = 'DD MMMM YYYY';

const breadcrumbItems = [
  {
    text: 'Modulo Poscontractual',
    to: `${INTERNAL_LINKS.POSTCONTRACTUAL}`,
  },
  {
    text: 'Formulario Poscontractual',
  },
];

const formatDate = (dateVal, strFormat) => {
  var dateFinal = dateVal ? dateVal : '';
  if (dateFinal !== '') {
    dateFinal = dayjs(dateFinal).format(strFormat);
  }

  return dateFinal;
};

const { Dragger } = Upload;

const EvidenceFields = ({ initialValues }) => {
  const [fileList, setFileList] = useState(
    initialValues.uploaded_files.map((f) => ({
      ...f,
      status: 'done',
      url: '#',
    })),
  );

  function onChange({ file, fileList }) {
    setFileList(fileList);
    const { status } = file;
    if (status === 'done') {
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: `${file.name} subido correctamente.`,
        duration: 2,
      });
      file.is_mine = true;
    } else if (status === 'error') {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: `${file.name} no se pudo subir.`,
        duration: 2,
      });
    }
  }

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      formData.append('terminacion', initialValues.id);
      formData.append('archivo', file);
      const res = await uploadEvidence(formData);
      onSuccess(res.data, file);
      setFileList((prevList) =>
        prevList.map((f) => (f.uid === file.uid ? res.data : f)),
      );
    } catch (error) {
      onError(error);
    }
  };

  const onPreview = async (file) => {
    try {
      const { data } = await getEvidence(file.uid);
      window.open(data.url, '_blank');
    } catch (error) {
      catchExceptionSRate(error);
    }
  };

  const onDelete = async (file) => {
    try {
      await deleteEvidence(file.uid);
    } catch (error) {
      catchExceptionSRate(error);
    }
  };

  return (
    <>
      <Form.Item
        name="requiere_soporte"
        label="Requiere soporte que justifique la realización de esta acta de liquidación"
        rules={[{ required: true }]}
      >
        <Radio.Group>
          <Radio
            value={true}
            disabled={[2, 4].includes(initialValues.tipo_terminacion)}
          >
            Sí
          </Radio>
          <Radio
            value={false}
            disabled={
              [2, 4].includes(initialValues.tipo_terminacion) ||
              fileList.length > 0
            }
          >
            No
          </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item noStyle dependencies={['requiere_soporte']}>
        {({ getFieldValue }) =>
          getFieldValue('requiere_soporte') ? (
            <Form.Item
              name="uploaded_evidence"
              rules={[
                {
                  required: fileList.length < 1,
                  message: 'Al menos un soporte es requerido!',
                },
              ]}
            >
              <Dragger
                customRequest={customRequest}
                fileList={fileList}
                name="file"
                multiple={true}
                onChange={onChange}
                onPreview={onPreview}
                onRemove={onDelete}
                showUploadList={{
                  showRemoveIcon: (file) => {
                    // Only allow removal if it was uploaded by me
                    return file?.is_mine;
                  },
                }}
                accept=".doc,.docx,.xls,.xlsx,image/*,application/pdf"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Presione o arrastre en esta área para subir el soporte
                </p>
                <p className="ant-upload-hint">
                  El tamaño máximo por la totalidad de archivos es de 10MB. Si
                  desea ver el contenido del archivo cargado, de clic en el
                  nombre.
                  <br />
                  El tipo de archivos que se pueden cargar son: pdf, doc, docx,
                  xls, xlsx e imágenes.
                </p>
              </Dragger>
            </Form.Item>
          ) : null
        }
      </Form.Item>
    </>
  );
};

const OrdinaryFields = ({ initialValues, sendPerm }) => {
  return (
    <>
      <Form.Item
        name="clausula_pago"
        label="Por favor, digite en letras el número de la cláusula del contrato de Forma de pago"
        rules={[{ required: true }]}
        labelCol={{ span: 24 }}
        wrapperCol={{ lg: 16, md: 24 }}
      >
        <Input />
      </Form.Item>
      <Descriptions
        layout="horizontal"
        column={2}
        bordered
        labelStyle={{ maxWidth: '10rem' }}
      >
        <Descriptions.Item label="Valor total pactado del contrato">
          <Form.Item name="valor_total" noStyle>
            <InputNumber
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
          <b>{initialValues.currency}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Valor total efectivo del contrato">
          <Form.Item name="valor_efectivo" noStyle>
            <InputNumber
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
          <b>{initialValues.currency}</b>
        </Descriptions.Item>

        <Descriptions.Item label="Fecha de inicio pactada">
          <Form.Item name="fecha_inicio" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_inicio, dateFormatText)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Fecha de inicio efectiva">
          <Form.Item name="fecha_inicio" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_inicio, dateFormatText)}</b>
        </Descriptions.Item>

        <Descriptions.Item label="Fecha de terminación pactada">
          <Form.Item name="fecha_fin" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_fin, dateFormatText)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Fecha de terminación efectiva">
          <Form.Item name="fecha_efectiva" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_efectiva, dateFormatText)}</b>
        </Descriptions.Item>

        <Descriptions.Item
          span={2}
          label="Observaciones exclusivas del acta de terminación a generar (visibles en acta)"
        >
          <Form.Item name="observaciones_acta" noStyle>
            <Input.TextArea
              rows={2}
              showCount
              maxLength={2500}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        </Descriptions.Item>
      </Descriptions>
      <br />
      <EvidenceFields initialValues={initialValues} />

      {sendPerm && (
        <Form.Item
          name="observaciones_internas"
          label="Observaciones internas del proceso de liquidación"
          rules={[{ required: true }]}
          labelCol={{ span: 24 }}
        >
          <Input.TextArea
            rows={2}
            showCount
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </Form.Item>
      )}
    </>
  );
};

const AnticipatedFields = ({ initialValues, sendPerm }) => {
  return (
    <>
      <Divider orientation="left">
        <b>
          JUSTIFICACIÓN DEL ACTA DE TERMINACIÓN{' '}
          {initialValues.tipo_terminacion === 2
            ? 'ANTICIPADA BILATERAL'
            : 'ANTICIPADA UNILATERAL'}
        </b>
      </Divider>
      <Descriptions layout="vertical" column={1} bordered>
        <Descriptions.Item label="Causal">
          {initialValues.causal_texto}
        </Descriptions.Item>
        <Descriptions.Item label="Nota">
          {initialValues.causal_nota}
        </Descriptions.Item>
      </Descriptions>
      <br />
      <EvidenceFields initialValues={initialValues} />

      {sendPerm && (
        <Form.Item
          name="observaciones_internas"
          label="Observaciones internas del proceso de liquidación"
          rules={[{ required: true }]}
          labelCol={{ span: 24 }}
        >
          <Input.TextArea
            rows={2}
            showCount
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </Form.Item>
      )}
      <Divider orientation="left">
        <b>BALANCE FINANCIERO DEL CONTRATO</b>
      </Divider>
      <Form.Item
        name="clausula_valor"
        label="Por favor, digite en letras el número de la cláusula del contrato de Valor"
        rules={[{ required: true }]}
        labelCol={{ span: 24 }}
        wrapperCol={{ lg: 16, md: 24 }}
      >
        <Input />
      </Form.Item>
      <Link
        to={`${INTERNAL_LINKS.PRODUCT_APPROVAL}-form/pc/${initialValues.contrato}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <CustomButton
          text="Ver aprobación de productos"
          type="default"
          htmlType="button"
          className={'ant-btn-info'}
        />
      </Link>
      <br />
      <br />
      <Descriptions
        layout="horizontal"
        column={1}
        bordered
        labelStyle={{ maxWidth: '10rem' }}
      >
        <Descriptions.Item label="Valor total del contrato (incluyendo modificaciones)">
          <Form.Item name="valor_total" noStyle>
            <InputNumber
              disabled
              style={{ minWidth: '20rem' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              suffix={<b>{initialValues.currency}</b>}
            />
          </Form.Item>
        </Descriptions.Item>
        <Descriptions.Item label="Valor ejecutado (aprobado)">
          <Form.Item name="valor_efectivo" noStyle>
            <InputNumber
              disabled
              style={{ minWidth: '20rem' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              suffix={<b>{initialValues.currency}</b>}
            />
          </Form.Item>
        </Descriptions.Item>

        <Descriptions.Item label="Valor ejecutado y pagado">
          <Form.Item
            name="valor_pagado"
            label="Valor ejecutado y pagado"
            labelCol={{ span: 0 }}
            rules={[
              { required: true },
              { type: 'number', min: 0, message: 'Ingrese un valor positivo' },
              {
                type: 'number',
                max: initialValues.valor_efectivo,
                message:
                  'El valor digitado debe ser menor o igual al Valor ejecutado del contrato.',
              },
            ]}
          >
            <InputNumber
              style={{ minWidth: '20rem' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              suffix={initialValues.currency}
            />
          </Form.Item>
        </Descriptions.Item>

        <Descriptions.Item label="Valor ejecutado y por pagar">
          <Form.Item noStyle dependencies={['valor_pagado']}>
            {({ getFieldValue, setFieldsValue }) => {
              setFieldsValue({
                valor_no_pagado:
                  initialValues.valor_efectivo -
                  (getFieldValue('valor_pagado') || 0),
              });
              return (
                <Form.Item name="valor_no_pagado" noStyle>
                  <InputNumber
                    disabled
                    style={{ minWidth: '20rem' }}
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    suffix={initialValues.currency}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Descriptions.Item>

        <Descriptions.Item label="Valor no ejecutado">
          <Form.Item name="valor_no_ejecutado" noStyle>
            <InputNumber
              disabled
              style={{ minWidth: '20rem' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              suffix={<b>{initialValues.currency}</b>}
            />
          </Form.Item>
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">
        <b>DURACIÓN Y OBSERVACIONES EXCLUSIVAS DEL ACTA</b>
      </Divider>
      <Form.Item
        name="clausula_duracion"
        label="Por favor, digite en letras el número de la cláusula del contrato de Duración"
        rules={[{ required: true }]}
        labelCol={{ span: 24 }}
        wrapperCol={{ lg: 16, md: 24 }}
      >
        <Input />
      </Form.Item>
      <Descriptions
        layout="horizontal"
        column={2}
        bordered
        labelStyle={{ maxWidth: '10rem' }}
      >
        <Descriptions.Item label="Fecha de inicio del contrato">
          <Form.Item name="fecha_inicio" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_inicio, dateFormatText)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Fecha de inicio efectiva">
          <Form.Item name="fecha_inicio" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_inicio, dateFormatText)}</b>
        </Descriptions.Item>

        <Descriptions.Item label="Fecha de terminación VIGENTE">
          <Form.Item name="fecha_fin" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_fin, dateFormatText)}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Fecha de terminación efectiva">
          <Form.Item name="fecha_efectiva" noStyle>
            <DatePicker
              format={dateFormat}
              disabled
              style={{ width: 'fit-content', marginRight: '10px' }}
            />
          </Form.Item>
          <b>{formatDate(initialValues.fecha_efectiva, dateFormatText)}</b>
        </Descriptions.Item>

        <Descriptions.Item
          span={2}
          label="Observaciones exclusivas del acta de terminación a generar (visibles en acta)"
        >
          <Form.Item name="observaciones_acta" noStyle>
            <Input.TextArea
              rows={2}
              showCount
              maxLength={2500}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        </Descriptions.Item>
      </Descriptions>
      <br />
    </>
  );
};

const PostContractualForm = ({
  match,
  history,
  sendPerm,
  followUp = false,
}) => {
  const [form] = Form.useForm();

  const [hasChanges, setHasChanges] = useState(false);

  const [initialValues, setInitialValues] = useState();

  const [loading, setLoading] = useState(false);

  const [toSign, setToSign] = useState(false);

  const [pdfURL, setPdfUrl] = useState(undefined);

  useEffect(() => {
    async function fetchData() {
      setInitialValues();
      try {
        const { data } = followUp
          ? await getEdit(match.params.id)
          : await get(match.params.id);
        setInitialValues({
          ...data,
          fecha_inicio: dayjs(data.fecha_inicio),
          fecha_fin: dayjs(data.fecha_fin),
          fecha_efectiva: dayjs(data.fecha_efectiva),
        });
      } catch (error) {
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_GET_MSG,
          duration: 2,
        });
      }
    }
    fetchData();
  }, [match.params.id]);

  const showConfirmation = (doRedirect) => {
    setHasChanges(false);
    Modal.success({
      title: MESSAGES.SUCCESS_MSG,
      content: (
        <>
          <Divider />
          <p>Se han registrado los cambios correctamente.</p>
          <p>
            Esta información siempre estará disponible en la sección de Módulo
            Poscontractual.
          </p>

          <p>Gracias</p>
        </>
      ),
      onOk: () => {
        if (doRedirect)
          history.push(
            `${INTERNAL_LINKS.POSTCONTRACTUAL}/0/${initialValues.proyecto_id}`,
          );
      },
      onCancel: () => {
        if (doRedirect)
          history.push(
            `${INTERNAL_LINKS.POSTCONTRACTUAL}/0/${initialValues.proyecto_id}`,
          );
      },
    });
  };

  const onSave = () => {
    form.submit();
  };

  const onSaveSend = () => {
    setToSign(true);
    form.submit();
  };

  const prepareValues = (values) => {
    const vc = { ...values };
    delete vc['uploaded_evidence'];
    delete vc['fecha_efectiva'];
    delete vc['fecha_fin'];
    delete vc['fecha_inicio'];
    delete vc['valor_efectivo'];
    delete vc['valor_total'];
    if (values?.clausulas_adicionales)
      vc['clausulas_texto'] = formatRTValue(values.clausulas_texto);
    return vc;
  };

  const onFinishForm = async (values) => {
    const pValues = prepareValues(values);
    if (toSign) onFinishSend(pValues);
    else
      try {
        setLoading(true);
        if (followUp) await putEdit(initialValues.id, pValues);
        else await put(initialValues.id, pValues);
        showConfirmation();
      } catch (error) {
        catchExceptionSRate(error, MESSAGES.DESC_ERROR_POST_MSG);
      } finally {
        setLoading(false);
      }
  };

  const onFinishSend = async (values) => {
    try {
      setLoading(true);
      const response = await sendSign(initialValues.id, values);
      if (response.data?.errors) {
        // Este modal se mostrara unicamente si la validacion de guardado es distinta a la de envio, (en caso de guardado parcial)
        // actualmente sin uso
        Modal.error({
          title: 'Validación de liquidación',
          content: (
            <>
              La liquidación requiere ajustes:
              <ErrorsContent errors={response.data?.errors} />
            </>
          ),
        });
        setToSign(false);
        setLoading(false);
      } else showConfirmation(true);
    } catch (error) {
      setToSign(false);
      setLoading(false);
      catchExceptionSRate(error, MESSAGES.DESC_ERROR_POST_MSG);
    }
  };

  const onTemplatePreview = async () => {
    setLoading(true);
    try {
      let pValues;
      try {
        pValues = prepareValues(await form.validateFields());
      } catch (formError) {
        // Scroll to first error manually due to manual validation above
        form.scrollToField(formError.errorFields[0].name);
        return;
      }
      const { data } = followUp
        ? previewActEdit(initialValues.id, pValues)
        : await previewAct(initialValues.id, pValues);
      setPdfUrl(data.url);
      setLoading(false);
    } catch (error) {
      catchExceptionSRate(error, MESSAGES.DESC_ERROR_POST_MSG);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (assignData) => {
    setLoading(true);
    try {
      let pValues;
      try {
        pValues = prepareValues(await form.validateFields());
      } catch (formError) {
        // Scroll to first error manually due to manual validation above
        form.scrollToField(formError.errorFields[0].name);
        setLoading(false);
        return 'error';
      }
      const { data } = await sendObservations(initialValues.id, {
        ...assignData,
        pcdata: pValues,
      });
      setLoading(false);
      return data;
    } catch (error) {
      catchExceptionSRate(error, MESSAGES.DESC_ERROR_POST_MSG);
      setLoading(false);
      return 'error';
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        MÓDULO POSCONTRACTUAL
      </Title>
      {initialValues ? (
        <>
          <Title level={3}>Proyecto: {initialValues.project_name}</Title>
          <Descriptions
            layout="horizontal"
            column={2}
            bordered
            labelStyle={{ maxWidth: '5rem' }}
          >
            <Descriptions.Item label="Contratista / Proveedor" span={2}>
              <Row align="middle">
                <Col flex="auto">{initialValues.contratista_nombre}</Col>
                <Col flex="0 0">
                  <CustomButton
                    htmlType="button"
                    text={
                      <Link
                        to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${initialValues.contrato}/${initialValues.proyecto_id}/${initialValues.cargo_id}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver históricos contractuales
                      </Link>
                    }
                  />
                </Col>
              </Row>
            </Descriptions.Item>
            <Descriptions.Item label="Cargo/Rol" span={2}>
              {initialValues.cargo_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="No. de contrato">
              {initialValues.contrato_num}
            </Descriptions.Item>
            <Descriptions.Item label="Cantidad de modificaciones realizadas">
              {initialValues.mod_num ?? 0}
            </Descriptions.Item>
          </Descriptions>
          <br />
          <Title level={4}>
            Terminación y Liquidación{' '}
            {initialValues.tipo_terminacion === 1
              ? 'Ordinaria Bilateral'
              : initialValues.tipo_terminacion === 2
                ? 'Anticipada Bilateral'
                : initialValues.tipo_terminacion === 3
                  ? 'Ordinaria Unilateral'
                  : 'Anticipada Unilateral'}
          </Title>
          <Form
            layout="horizontal"
            initialValues={initialValues}
            form={form}
            onFinish={onFinishForm}
            onValuesChange={() => setHasChanges(true)}
            preserve={false}
            scrollToFirstError
          >
            {[1, 3].includes(initialValues.tipo_terminacion) ? (
              <OrdinaryFields
                initialValues={initialValues}
                sendPerm={sendPerm}
              />
            ) : (
              <AnticipatedFields
                initialValues={initialValues}
                sendPerm={sendPerm}
              />
            )}
            {sendPerm && (
              <>
                <Form.Item
                  name="clausulas_adicionales"
                  label="Desea agregarle nuevas cláusulas/acuerdos al acta de terminación y liquidación"
                  tooltip={{
                    placement: 'top',
                    title:
                      'Tener en cuenta que el texto aquí indicado debe ser armónico con el resto del acta',
                  }}
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Radio value={true}>Sí</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item noStyle dependencies={['clausulas_adicionales']}>
                  {({ getFieldValue }) =>
                    getFieldValue('clausulas_adicionales') ? (
                      <>
                        <b>NOTA:</b>
                        <span>
                          {' '}
                          Recuerde verificar si hay clausulas/acuerdos
                          consecutivos para esta terminación. El texto agregado
                          debe ser coherente con el cuerpo del documento.
                        </span>
                        <br />
                        <br />
                        <Form.Item name="clausulas_texto" rules={[getRTRule]}>
                          <RichTextArea placeholder="Digite las cláusulas/acuerdos adicionales" />
                        </Form.Item>
                      </>
                    ) : null
                  }
                </Form.Item>
              </>
            )}
          </Form>
          <Row align="middle">
            <Col span={sendPerm ? 8 : 12} align="center">
              <Space>
                <CustomButton
                  onClick={onSave}
                  disabled={loading}
                  loading={loading && !toSign}
                  htmlType="button"
                  text="GUARDAR TERMINACIÓN"
                />
              </Space>
            </Col>
            {sendPerm && (
              <Col span={8} align="center">
                <Space>
                  <CustomButton
                    onClick={onSaveSend}
                    disabled={loading}
                    loading={loading && toSign}
                    type="primary"
                    htmlType="button"
                    text="GUARDAR Y ENVIAR PARA FIRMA"
                  />
                </Space>
              </Col>
            )}
            <Col span={sendPerm ? 8 : 12} align="center">
              <CustomButton
                type="default"
                onClick={onTemplatePreview}
                disabled={loading}
                loading={loading && !toSign}
                htmlType="button"
                text="Previsualizar"
              />
            </Col>
          </Row>
          <Divider />
          {/* {!hasChanges && ( */}
          <Row align="middle">
            <Col flex="auto" span={24} align="left">
              <AssignmentStates
                postcontractualID={initialValues.id}
                onAssign={handleAssign}
                projectID={initialValues.proyecto_id}
              />
            </Col>
          </Row>
          {/* )} */}
          <CustomButton
            htmlType="button"
            className={'back-button'}
            type="default"
            text={
              <Link
                to={`${INTERNAL_LINKS.POSTCONTRACTUAL}/0/${initialValues.proyecto_id}`}
              >
                Volver
              </Link>
            }
          />
        </>
      ) : (
        <SpinLoad loading={true} />
      )}
      <PdfViewer
        pdf={pdfURL}
        onCancel={() => setPdfUrl()}
        open={!!pdfURL}
        isWord
      />
    </>
  );
};

export default PostContractualForm;
